import dayjs from 'dayjs';

const BRAND = '#3B82F6';
const BRAND_SOFT = '#BFDBFE';

const PIE_PALETTE = ['#3B82F6', '#0EA5E9', '#6366F1', '#14B8A6', '#8B5CF6', '#06B6D4', '#1D4ED8'];

const isDark = () =>
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('dark');

export const buildAreaChartOptions = ({
  categories,
  dates,
  mode,
  isEmpty,
}) => ({
  legend: { show: false },
  colors: [BRAND],
  fill: {
    type: 'gradient',
    gradient: {
      shade: 'light',
      type: 'vertical',
      shadeIntensity: 0.4,
      gradientToColors: [BRAND_SOFT],
      inverseColors: false,
      opacityFrom: 0.55,
      opacityTo: 0.05,
      stops: [0, 100],
    },
  },
  grid: {
    show: true,
    borderColor: 'rgba(148, 163, 184, 0.18)',
    strokeDashArray: 4,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
    padding: { top: 10, right: 16, bottom: 0, left: 8 },
  },
  chart: {
    height: 320,
    type: 'area',
    toolbar: { show: false },
    fontFamily: 'Lato, sans-serif',
    animations: { enabled: true, easing: 'easeinout', speed: 600 },
  },
  dataLabels: { enabled: false },
  stroke: { curve: 'smooth', width: 2.5 },
  markers: {
    size: 0,
    strokeColors: BRAND,
    strokeWidth: 2,
    hover: { size: 6 },
  },
  yaxis: {
    min: 0,
    forceNiceScale: true,
    ...(isEmpty ? { max: 10, tickAmount: 5 } : {}),
    labels: {
      show: true,
      offsetX: -8,
      style: { fontSize: '0.72rem', colors: '#94A3B8', fontWeight: 600 },
      formatter: (value) =>
        mode === 'Sales'
          ? `$${Number(value || 0).toFixed(0)}`
          : `${value >= 1 || value === 0 ? Number(value || 0).toFixed(0) : Number(value).toFixed(1)}`,
    },
  },
  xaxis: {
    type: 'category',
    categories,
    labels: {
      style: { fontSize: '0.72rem', colors: '#94A3B8', fontWeight: 600 },
      offsetY: 4,
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  tooltip: {
    theme: 'light',
    x: {
      // Eksen gün adını ("Wed") gösterir; tooltip başlığında gerçek tarihi
      // göster (ör. "May 8"). dates, categories ile aynı sıradadır.
      formatter: (value, opts) => {
        const idx = opts?.dataPointIndex;
        const date = dates && idx != null ? dates[idx] : null;
        return date ? dayjs(date).format('MMM D') : value;
      },
    },
    y: {
      formatter: (value) => {
        if (value === null || value === undefined) return '';
        return mode === 'Sales'
          ? `$${Number(value).toFixed(2)}`
          : `${value} items`;
      },
    },
  },
});

const safeLabels = (labels) =>
  (labels || []).map((l, i) => {
    const v = (l ?? '').toString().trim();
    return v.length > 0 ? v : `Channel ${i + 1}`;
  });

export const buildPieChartOptions = (labels) => ({
  chart: {
    width: 380,
    type: 'pie',
    fontFamily: 'Lato, sans-serif',
  },
  colors: PIE_PALETTE,
  labels: safeLabels(labels),
  legend: {
    show: true,
    position: 'bottom',
    horizontalAlign: 'center',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'Lato, sans-serif',
    labels: {
      colors: isDark() ? '#cbd5e1' : '#475569',
      useSeriesColors: false,
    },
    markers: { width: 10, height: 10, radius: 3, offsetX: -2 },
    itemMargin: { horizontal: 10, vertical: 6 },
    formatter: (seriesName, opts) => {
      const value = opts?.w?.globals?.series?.[opts.seriesIndex] ?? 0;
      return `${seriesName}  ·  ${value}`;
    },
  },
  stroke: { width: 2, colors: [isDark() ? '#1e293b' : '#fff'] },
  dataLabels: {
    style: { fontSize: '0.72rem', fontWeight: 700, colors: ['#fff'] },
    dropShadow: { enabled: false },
  },
  tooltip: {
    y: {
      formatter: (val) => `${val} orders`,
    },
  },
  responsive: [
    {
      breakpoint: 480,
      options: { chart: { width: '100%' } },
    },
  ],
});

export const EMPTY_PIE_OPTIONS = (label = 'No data') =>
  buildPieChartOptions([label]);
